import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Table } from '@tanstack/react-table';
import { Square, SquareCheckBig, SquareMinus } from 'lucide-react';

interface TableSelectionHeaderProps<TData> {
  table: Table<TData>;
}

/**
 * TableSelectionHeader - Renders the select-all checkbox in the table header
 *
 * This component only handles row selection. Table controls (columns, sorting,
 * bulk edit) are now managed by TableControlsButton in the toolbar.
 */
export function TableSelectionHeader<TData>({
  table,
}: TableSelectionHeaderProps<TData>) {
  return (
    <div className="absolute inset-0 flex items-stretch justify-end gap-1 align-middle">
      <div
        className={cn(
          'flex grow-0 pl-1',
          'opacity-0 transition duration-200',
          'to-card bg-gradient-to-r from-transparent to-10%',
          'group-data-[state=selected]/row:to-muted',
          'group-data-[select-mode=active]/table:opacity-100',
          // Only show on hover for desktop (md = 768px+)
          'md:group-hover/row:opacity-100',
          'md:group-hover/row:to-(--muted-half)',
          'md:group-data-[state=selected]/row:group-hover/row:to-muted',
        )}
      >
        <Button
          variant="ghost"
          className={cn(
            // Larger tap target on mobile (48px), standard on desktop (32px)
            'h-full w-12 p-0 md:w-8',
            // Center the icon within the button
            'flex items-center justify-center',
          )}
          onClick={() => table.toggleAllRowsSelected()}
          aria-label={
            table.getIsAllRowsSelected()
              ? 'Deselect all rows'
              : 'Select all rows'
          }
          tabIndex={0}
        >
          {(() => {
            if (table.getIsSomeRowsSelected()) {
              return (
                <SquareMinus
                  className={cn(
                    'opacity-0 transition-opacity duration-200',
                    'group-data-[select-mode=active]/table:opacity-100',
                    'md:group-hover/row:opacity-40',
                  )}
                />
              );
            } else if (table.getIsAllRowsSelected()) {
              return (
                <SquareCheckBig
                  className={cn(
                    'opacity-0 transition-opacity duration-200',
                    'group-data-[select-mode=active]/table:opacity-100',
                    'md:group-hover/row:opacity-40',
                  )}
                />
              );
            } else {
              return (
                <Square
                  className={cn(
                    'opacity-0 transition-opacity duration-200',
                    'group-data-[select-mode=active]/table:opacity-100',
                    'md:group-hover/row:opacity-40',
                  )}
                />
              );
            }
          })()}
        </Button>
      </div>
    </div>
  );
}
